<template>
  <v-container fluid>
    <v-row v-if="!dialogOnly && !errorMessage">
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="items"
          :search="search"
          :options.sync="options"
          :server-items-length="totalItems"
          :loading="apiLoading"
          :loading-text="$t('wkt.admin.common.table.loading')"
          :no-data-text="$t('wkt.admin.common.table.no_data')"
          :no-results-text="$t('wkt.admin.common.table.no_results')"
          :footer-props="{
            'items-per-page-options': [20, 50, 100],
            'items-per-page-text': $t('wkt.admin.common.table.items_per_page'),
            'items-per-page-all-text': $t('wkt.admin.common.table.items_per_page_all')
          }"
          class="elevation-0"
          :fixed-header="true"
        >
          <template v-slot:top>
            <v-toolbar flat color="white" class="mb-3 tasks-list-toolbar">
              <v-row align="center">
                <v-col cols="12" md="8">
                  <div class="d-flex flex-column flex-md-row flex-md-wrap align-md-center">
                    <v-text-field
                      v-model="search"
                      :label="$t('wkt.admin.tasks.table.search')"
                      hide-details
                      clearable
                      class="tasks-search-field mr-md-3"
                      @click:clear="searchTimeOut()"
                      @keyup="searchTimeOut()"
                    />
                    <v-btn
                      class="tasks-hidden-button mt-3 mt-md-0"
                      :color="showHiddenTasks ? 'primary' : undefined"
                      :outlined="!showHiddenTasks"
                      :dark="showHiddenTasks"
                      @click="toggleHiddenTasks"
                    >
                      {{ $t('wkt.admin.tasks.actions.view_hidden') }}
                    </v-btn>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="text-md-right">
                  <v-btn
                    v-if="canWrite"
                    color="primary"
                    depressed
                    @click="openNewTask"
                  >
                    {{ $t('wkt.admin.tasks.actions.new') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-toolbar>
          </template>

          <template v-slot:[`item`]="{ item }">
            <tr
              class="tasks-row"
              :class="{ 'tasks-row--unread': item.hasUnreadActivity }"
              @click="openTask(item)"
            >
              <td>
                <div class="d-flex align-center">
                  <span v-if="item.hasUnreadActivity" class="task-novelty-dot mr-3" />
                  <div>
                    <div class="d-flex align-center flex-wrap">
                      <div
                        style="font-weight: 500;"
                        :class="{
                          'error--text': isListTaskOverdue(item),
                          'warning--text': !isListTaskOverdue(item) && hasListTaskReminderAlert(item)
                        }"
                      >
                        {{ item.title }}
                      </div>
                      <v-chip
                        v-if="hasListTaskReminderAlert(item)"
                        x-small
                        color="warning"
                        text-color="white"
                        class="ml-2"
                      >
                        {{ $t('wkt.admin.tasks.views.with_alert') }}
                      </v-chip>
                      <v-chip
                        v-if="isListTaskOverdue(item)"
                        x-small
                        color="error"
                        text-color="white"
                        class="ml-2"
                      >
                        {{ $t('wkt.admin.tasks.summary.overdue') }}
                      </v-chip>
                    </div>
                    <div v-if="item.description" class="grey--text text--darken-1">{{ item.description }}</div>
                  </div>
                </div>
              </td>
              <td>{{ formatTaskStatus(item.status) }}</td>
              <td>{{ item.dueDate ? formatLongDateTime(item.dueDate, true) : '--' }}</td>
              <td>{{ item.updateDate ? formatLongDateTime(item.updateDate, true) : formatLongDateTime(item.creationDate, true) }}</td>
            </tr>
          </template>

          <template v-slot:[`footer.page-text`]="props">
            {{ props.pageStart }} - {{ props.pageStop }} {{ $t('wkt.admin.paged-tables.pager.of') }} {{ props.itemsLength > props.pageStop ? `+${props.pageStop}` : props.pageStop }}
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog v-model="detailDialog" max-width="1200" persistent scrollable>
      <v-card>
        <v-toolbar flat class="task-dialog-toolbar">
          <div class="d-flex align-center flex-wrap">
            <v-toolbar-title class="mr-3">
              {{ selectedTask && selectedTask.task && selectedTask.task.id ? $t('wkt.admin.tasks.dialog.edit_title') : $t('wkt.admin.tasks.dialog.create_title') }}
            </v-toolbar-title>
            <v-chip small color="primary" text-color="white" class="mr-2 mb-1 mt-1">
              {{ formatTaskStatus(taskForm.status) }}
            </v-chip>
            <v-chip
              v-if="selectedTaskHasUnreadActivity"
              small
              outlined
              color="primary"
              class="mr-2 mb-1 mt-1"
            >
              {{ $t('wkt.admin.tasks.views.with_news') }}
            </v-chip>
            <v-chip
              v-if="selectedTaskHasReminderAlert"
              small
              outlined
              color="warning"
              class="mr-2 mb-1 mt-1"
            >
              {{ $t('wkt.admin.tasks.views.with_alert') }}
            </v-chip>
            <v-chip
              v-if="isTaskOverdue"
              small
              outlined
              color="error"
              class="mr-2 mb-1 mt-1"
            >
              {{ $t('wkt.admin.tasks.summary.overdue') }}
            </v-chip>
          </div>
          <v-spacer />
          <div class="d-flex align-center flex-wrap">
            <v-btn
              icon
              color="grey darken-4"
              :title="$t('wkt.admin.common.dialog-buttons.close')"
              @click="closeDetailDialog"
            >
              <v-icon color="grey darken-4">close</v-icon>
            </v-btn>
          </div>
        </v-toolbar>

        <v-divider />

        <v-card-text style="min-height: 540px;" @wheel="onWheel">
          <v-skeleton-loader
            v-if="detailLoading"
            type="article, article, article"
          />

          <div v-else>
            <v-tabs v-model="detailTab" grow>
              <v-tab>{{ $t('wkt.admin.tasks.tabs.details') }}</v-tab>
              <v-tab :disabled="isCreatingTask">{{ `${$t('wkt.admin.tasks.sections.comments')} (${taskComments.length})` }}</v-tab>
              <v-tab :disabled="isCreatingTask">{{ `${$t('wkt.admin.tasks.sections.timeline')} (${timelineItems.length})` }}</v-tab>
            </v-tabs>

            <v-tabs-items v-model="detailTab">
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-form ref="taskForm" lazy-validation>
                      <v-row>
                        <v-col cols="12" md="8">
                          <v-text-field
                            v-model="taskForm.title"
                            :label="$t('wkt.admin.tasks.fields.title')"
                            :rules="[validationRules.required]"
                            :readonly="!canWrite"
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-select
                            v-model="taskForm.status"
                            :items="statusItems.filter(x => x.value >= 0)"
                            item-text="text"
                            item-value="value"
                            :label="$t('wkt.admin.tasks.fields.status')"
                            :menu-props="taskDialogMenuProps"
                            :readonly="!canWrite"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <v-textarea
                            v-model="taskForm.description"
                            :label="$t('wkt.admin.tasks.fields.description')"
                            auto-grow
                            rows="4"
                            :readonly="!canWrite"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12">
                          <v-autocomplete
                            v-model="taskForm.assigneeEmployeeIds"
                            :items="employees"
                            :item-text="employeeText"
                            item-value="id"
                            :label="$t('wkt.admin.tasks.fields.assignees')"
                            multiple
                            chips
                            clearable
                            deletable-chips
                            :menu-props="taskDialogMenuProps"
                            :readonly="!canWrite"
                            :rules="[validationRules.requiredMultiple]"
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="12" md="6">
                          <v-row dense>
                            <v-col cols="12" md="7">
                              <v-menu
                                v-model="dueDateMenu"
                                :close-on-content-click="true"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                                :content-class="taskDialogMenuProps.contentClass"
                                :disabled="!canWrite"
                              >
                                <template v-slot:activator="{ on }">
                                  <v-text-field
                                    :value="getLocalDatePart(taskForm.dueDate)"
                                    :label="$t('wkt.admin.tasks.fields.due_date')"
                                    readonly
                                    :disabled="!canWrite"
                                    v-on="on"
                                  />
                                </template>
                                <v-date-picker
                                  :value="getLocalDatePart(taskForm.dueDate)"
                                  no-title
                                  scrollable
                                  @input="updateTaskDatePart('dueDate', $event); dueDateMenu = false"
                                />
                              </v-menu>
                            </v-col>
                            <v-col cols="12" md="5">
                              <v-menu
                                v-model="dueTimeMenu"
                                :close-on-content-click="false"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                                :content-class="taskDialogMenuProps.contentClass"
                                :disabled="!canWrite"
                              >
                                <template v-slot:activator="{ on }">
                                  <v-text-field
                                    :value="getLocalTimePart(taskForm.dueDate)"
                                    :label="$t('wkt.admin.qbuilder.time')"
                                    readonly
                                    :disabled="!canWrite"
                                    v-on="on"
                                  />
                                </template>
                                <v-time-picker
                                  format="24hr"
                                  scrollable
                                  :value="getLocalTimePart(taskForm.dueDate)"
                                  @input="updateTaskTimePart('dueDate', $event)"
                                />
                              </v-menu>
                            </v-col>
                          </v-row>
                          <div class="d-flex flex-wrap mt-2">
                            <v-chip
                              v-if="canWrite"
                              small
                              outlined
                              class="mr-2 mb-2"
                              @click="applyDueDatePreset(0)"
                            >
                              {{ $t('wkt.admin.tasks.quick_due.today') }}
                            </v-chip>
                            <v-chip
                              v-if="canWrite"
                              small
                              outlined
                              class="mr-2 mb-2"
                              @click="applyDueDatePreset(1)"
                            >
                              {{ $t('wkt.admin.tasks.quick_due.tomorrow') }}
                            </v-chip>
                            <v-chip
                              v-if="canWrite"
                              small
                              outlined
                              class="mr-2 mb-2"
                              @click="applyDueDatePreset(3)"
                            >
                              {{ $t('wkt.admin.tasks.quick_due.in_3_days') }}
                            </v-chip>
                            <v-chip
                              v-if="canWrite"
                              small
                              outlined
                              class="mr-2 mb-2"
                              @click="clearDueDate"
                            >
                              {{ $t('wkt.admin.tasks.quick_due.clear') }}
                            </v-chip>
                          </div>
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-row align="center">
                            <v-col cols="12" md="4">
                              <v-switch
                                v-model="taskForm.reminderEnabled"
                                :label="$t('wkt.admin.tasks.fields.reminder_enabled')"
                                hide-details
                                class="mt-0 pt-0"
                                :readonly="!canWrite || !taskForm.dueDate"
                                :disabled="!taskForm.dueDate"
                              />
                            </v-col>
                            <v-col cols="12" md="8">
                              <v-select
                                v-model="taskForm.reminderOffsetMinutes"
                                :items="reminderOffsetItems"
                                item-text="text"
                                item-value="value"
                                :label="$t('wkt.admin.tasks.fields.reminder_offset')"
                                :menu-props="taskDialogMenuProps"
                                hide-details
                                :readonly="!canWrite || !taskForm.reminderEnabled || !taskForm.dueDate"
                                :disabled="!taskForm.reminderEnabled || !taskForm.dueDate"
                              />
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>

                      <v-expansion-panels
                        v-model="detailSectionPanels"
                        multiple
                        flat
                      >

                        <v-expansion-panel>
                          <v-expansion-panel-header>
                            {{ $t('wkt.admin.tasks.sections.advanced_visibility') }}
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <v-row>
                              <v-col cols="12" md="6">
                                <v-row dense>
                                  <v-col cols="12" md="7">
                                    <v-menu
                                      v-model="scheduledDateMenu"
                                      :close-on-content-click="true"
                                      transition="scale-transition"
                                      top
                                      offset-y
                                      min-width="290px"
                                      attach
                                      :content-class="taskDialogMenuProps.contentClass"
                                      :disabled="!canWrite"
                                    >
                                      <template v-slot:activator="{ on }">
                                        <v-text-field
                                          :value="getLocalDatePart(taskForm.scheduledDate)"
                                          :label="$t('wkt.admin.tasks.fields.scheduled_date')"
                                          :hint="$t('wkt.admin.tasks.fields.scheduled_date_hint')"
                                          persistent-hint
                                          readonly
                                          :disabled="!canWrite"
                                          v-on="on"
                                        />
                                      </template>
                                      <v-date-picker
                                        :value="getLocalDatePart(taskForm.scheduledDate)"
                                        no-title
                                        scrollable
                                        @input="updateTaskDatePart('scheduledDate', $event); scheduledDateMenu = false"
                                      />
                                    </v-menu>
                                  </v-col>
                                  <v-col cols="12" md="5">
                                    <v-menu
                                      v-model="scheduledTimeMenu"
                                      :close-on-content-click="false"
                                      transition="scale-transition"
                                      top
                                      offset-y
                                      min-width="290px"
                                      attach
                                      :content-class="taskDialogMenuProps.contentClass"
                                      :disabled="!canWrite"
                                    >
                                      <template v-slot:activator="{ on }">
                                        <v-text-field
                                          :value="getLocalTimePart(taskForm.scheduledDate)"
                                          :label="$t('wkt.admin.qbuilder.time')"
                                          readonly
                                          :disabled="!canWrite"
                                          v-on="on"
                                        />
                                      </template>
                                      <v-time-picker
                                        format="24hr"
                                        scrollable
                                        :value="getLocalTimePart(taskForm.scheduledDate)"
                                        @input="updateTaskTimePart('scheduledDate', $event)"
                                      />
                                    </v-menu>
                                  </v-col>
                                </v-row>
                                <div class="d-flex flex-wrap mt-2">
                                  <v-chip
                                    v-if="canWrite"
                                    small
                                    outlined
                                    class="mr-2 mb-2"
                                    @click="setScheduledDateNow"
                                  >
                                    {{ $t('wkt.admin.tasks.quick_schedule.visible_now') }}
                                  </v-chip>
                                  <v-chip
                                    v-if="canWrite"
                                    small
                                    outlined
                                    class="mr-2 mb-2"
                                    @click="applyScheduledPresetTomorrowMorning"
                                  >
                                    {{ $t('wkt.admin.tasks.quick_schedule.tomorrow_morning') }}
                                  </v-chip>
                                </div>
                              </v-col>
                            </v-row>
                          </v-expansion-panel-content>
                        </v-expansion-panel>

                        <v-expansion-panel v-if="!isCreatingTask">
                          <v-expansion-panel-header>
                            {{ $t('wkt.admin.tasks.sections.metadata') }}
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <v-simple-table dense>
                              <tbody>
                                <tr>
                                  <td class="font-weight-medium">{{ $t('wkt.admin.tasks.metadata.creator') }}</td>
                                  <td>{{ getEmployeeName(selectedTask && selectedTask.task ? selectedTask.task.creatorId : null) }}</td>
                                </tr>
                                <tr>
                                  <td class="font-weight-medium">{{ $t('wkt.admin.tasks.metadata.completed_by') }}</td>
                                  <td>{{ getEmployeeName(selectedTask && selectedTask.task ? selectedTask.task.completedByEmployeeId : null) }}</td>
                                </tr>
                                <tr>
                                  <td class="font-weight-medium">{{ $t('wkt.admin.tasks.metadata.workflow') }}</td>
                                  <td>{{ selectedTask && selectedTask.task && selectedTask.task.workflowId ? `#${selectedTask.task.workflowId}` : '--' }}</td>
                                </tr>
                                <tr>
                                  <td class="font-weight-medium">{{ $t('wkt.admin.tasks.metadata.created_at') }}</td>
                                  <td>{{ selectedTask && selectedTask.task ? formatLongDateTime(selectedTask.task.creationDate, true) : '--' }}</td>
                                </tr>
                                <tr>
                                  <td class="font-weight-medium">{{ $t('wkt.admin.tasks.metadata.updated_at') }}</td>
                                  <td>{{ selectedTask && selectedTask.task && selectedTask.task.updateDate ? formatLongDateTime(selectedTask.task.updateDate, true) : '--' }}</td>
                                </tr>
                                <tr>
                                  <td class="font-weight-medium">{{ $t('wkt.admin.tasks.metadata.completed_at') }}</td>
                                  <td>{{ selectedTask && selectedTask.task && selectedTask.task.completionDate ? formatLongDateTime(selectedTask.task.completionDate, true) : '--' }}</td>
                                </tr>
                              </tbody>
                            </v-simple-table>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-form>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-alert
                      v-if="isCreatingTask"
                      dense
                      outlined
                      type="info"
                    >
                      {{ $t('wkt.admin.tasks.empty.save_before_comments') }}
                    </v-alert>

                    <v-alert
                      v-else-if="!taskComments.length"
                      dense
                      outlined
                      type="info"
                    >
                      {{ $t('wkt.admin.tasks.empty.comments') }}
                    </v-alert>

                    <v-row>
                      <v-col cols="12" md="7">
                        <v-card outlined class="mb-4 mb-md-0 task-comments-history-card">
                          <v-card-text>
                            <div class="text-subtitle-2 font-weight-medium mb-3">
                              {{ $t('wkt.admin.tasks.comments.history') }}
                            </div>

                            <v-card
                              v-for="comment in taskComments"
                              :key="`comment-${comment.id}`"
                              outlined
                              class="mb-3"
                            >
                              <v-card-text>
                                <div class="d-flex align-center justify-space-between mb-2">
                                  <div class="font-weight-medium">{{ getEmployeeName(comment.employeeId) }}</div>
                                  <div class="grey--text text--darken-1">{{ formatLongDateTime(comment.creationDate, true) }}</div>
                                </div>
                                <div style="white-space: pre-wrap;">{{ comment.content }}</div>
                                <div v-if="getCommentAttachments(comment.id).length" class="mt-3">
                                  <div class="text-caption grey--text text--darken-1 mb-2">
                                    {{ $t('wkt.admin.tasks.comments.attachments') }}
                                  </div>
                                  <div class="d-flex flex-wrap">
                                    <v-chip
                                      v-for="attachment in getCommentAttachments(comment.id)"
                                      :key="`attachment-${attachment.id}`"
                                      class="mr-2 mb-2"
                                      outlined
                                      small
                                      @click="downloadAttachment(comment.id, attachment)"
                                    >
                                      <v-icon left small>attach_file</v-icon>
                                      {{ attachment.originalName }}
                                    </v-chip>
                                  </div>
                                </div>
                              </v-card-text>
                            </v-card>
                          </v-card-text>
                        </v-card>
                      </v-col>

                      <v-col cols="12" md="5">
                        <v-card outlined class="task-comment-composer-card">
                          <v-card-text>
                            <div class="text-subtitle-2 font-weight-medium mb-3">
                              {{ $t('wkt.admin.tasks.comments.write') }}
                            </div>

                            <v-textarea
                              v-model="newComment"
                              :label="$t('wkt.admin.tasks.fields.new_comment')"
                              auto-grow
                              rows="4"
                              :readonly="!canWrite"
                            />

                            <v-file-input
                              v-model="newCommentFiles"
                              :label="$t('wkt.admin.tasks.fields.comment_attachments')"
                              multiple
                              chips
                              small-chips
                              prepend-icon="attach_file"
                              :readonly="!canWrite"
                              show-size
                              @change="validateCommentAttachments"
                            />

                            <div class="text-right">
                              <v-btn
                                color="primary"
                                depressed
                                :disabled="!canWrite || !newComment.trim()"
                                :loading="commentLoading"
                                @click="saveComment"
                              >
                                {{ $t('wkt.admin.tasks.actions.add_comment') }}
                              </v-btn>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-alert
                      v-if="isCreatingTask"
                      dense
                      outlined
                      type="info"
                    >
                      {{ $t('wkt.admin.tasks.empty.save_before_timeline') }}
                    </v-alert>

                    <v-alert
                      v-else-if="!timelineItems.length"
                      dense
                      outlined
                      type="info"
                    >
                      {{ $t('wkt.admin.tasks.empty.timeline') }}
                    </v-alert>

                    <v-timeline dense align-top>
                      <v-timeline-item
                        v-for="item in timelineItems"
                        :key="item.key"
                        :color="(item.type === 'comment' || item.isHuman) ? 'primary' : 'grey darken-1'"
                        small
                        :icon="item.type === 'comment' ? 'chat_bubble_outline' : 'settings'"
                      >
                        <v-card
                          outlined
                          :class="(item.type === 'comment' || item.isHuman) ? 'task-activity-card task-activity-card--human' : 'task-activity-card task-activity-card--system'"
                        >
                          <v-card-text>
                            <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                              <div class="font-weight-medium mr-3">
                                {{ item.title }}
                              </div>
                              <v-chip
                                x-small
                                outlined
                                :color="(item.type === 'comment' || item.isHuman) ? 'primary' : 'grey darken-1'"
                              >
                                {{
                                  (item.type === 'comment' || item.isHuman)
                                    ? $t('wkt.admin.tasks.activity.human')
                                    : $t('wkt.admin.tasks.activity.system')
                                }}
                              </v-chip>
                            </div>
                            <div class="grey--text text--darken-1 mb-1">
                              {{ formatLongDateTime(item.date, true) }}
                            </div>
                            <div v-if="item.actor" class="caption grey--text text--darken-1 mb-1">
                              {{ item.actor }}
                            </div>
                            <template v-if="item.changes && item.changes.length">
                              <div
                                v-for="(change, ci) in item.changes"
                                :key="ci"
                                class="caption grey--text text--darken-2 mt-1"
                              >
                                {{ formatTaskChangeField(change) }}
                              </div>
                            </template>
                            <div v-else-if="item.content" style="white-space: pre-wrap;">{{ item.content }}</div>
                          </v-card-text>
                        </v-card>
                      </v-timeline-item>
                    </v-timeline>
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-btn
            v-if="canWrite && selectedTask && selectedTask.task && selectedTask.task.id"
            color="error"
            depressed
            :loading="deleteLoading"
            @click="deleteSelectedTask"
          >
            <span
              style="display: inline-flex; align-items: center; margin-right: 6px; color: #ffffff;"
              aria-hidden="true"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.79 3.29C14.61 3.11 14.34 3 14.07 3H9.93C9.66 3 9.39 3.11 9.21 3.29L8.5 4H5V6H19V4Z"/>
              </svg>
            </span>
            {{ $t('wkt.admin.common.dialog-buttons.delete') }}
          </v-btn>
          <v-spacer />
          <v-btn text @click="closeDetailDialog">
            {{ $t('wkt.admin.common.dialog-buttons.cancel') }}
          </v-btn>
          <v-btn
            v-if="canWrite"
            color="primary"
            depressed
            :loading="saveLoading"
            :disabled="detailLoading || commentLoading || pendingSaveAfterTabSwitch"
            @click="saveTask"
          >
            {{ isCreatingTask ? $t('wkt.admin.tasks.actions.create') : $t('wkt.admin.common.dialog-buttons.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="showAlert"
      :timeout="alertTimeout"
      :color="alertColor">
      <span style="color: #ffffff;">{{ alertText }}</span>
      <v-btn text @click="showAlert = false">
        <span style="color: #ffffff;">{{ $t('wkt.admin.common.dialog-buttons.close') }}</span>
      </v-btn>
    </v-snackbar>

    <ConfirmDialog
      ref="deleteTaskConfirmDialog"
      :title="$t('wkt.admin.common.dialog-buttons.delete')"
      :message="$t('wkt.admin.tasks.messages.delete_confirm')"
      :title-ok="$t('wkt.admin.common.dialog-buttons.delete')"
      :title-cancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="confirmDeleteSelectedTask"
    />
  </v-container>
</template>

<script>
import moment from 'moment'
import BaseModule from '../common/BaseModule.js'
import ConfirmDialog from '../common/ConfirmDialog.vue'
import api from '../../api/worktime'
import gwt from '../../api/gwt'

export default {
  name: 'TasksModule',
  components: {
    ConfirmDialog,
  },
  props: {
    dialogOnly: {
      type: Boolean,
      default: false,
    }
  },
  mixins: [BaseModule],
  data () {
    return {
      apiLoading: false,
      errorMessage: null,
      showAlert: false,
      alertColor: 'error',
      alertText: '',
      alertTimeout: 3000,
      search: '',
      showHiddenTasks: false,
      onlyAssignedToMe: true,
      statusFilter: -1,
      options: { itemsPerPage: 20 },
      totalItems: 0,
      items: [],
      employees: [],
      sessionProfile: null,
      sessionEmployeeId: null,
      realtimeRefreshTimer: null,
      canWrite: false,
      detailDialog: false,
      detailLoading: false,
      saveLoading: false,
      commentLoading: false,
      deleteLoading: false,
      detailTab: 0,
      pendingSaveAfterTabSwitch: false,
      pendingSaveAfterTabSwitchTimer: null,
      detailSectionPanels: [0],
      selectedTask: null,
      taskForm: {
        id: null,
        title: '',
        description: '',
        status: 0,
        scheduledDate: '',
        dueDate: '',
        reminderEnabled: false,
        reminderOffsetMinutes: 60,
        assigneeEmployeeIds: [],
      },
      newComment: '',
      newCommentFiles: [],
      headers: [
        { text: this.$t('wkt.admin.tasks.table.headers.title'), value: 'title', width: '50%' },
        { text: this.$t('wkt.admin.tasks.table.headers.status'), value: 'status', width: '15%' },
        { text: this.$t('wkt.admin.tasks.table.headers.due_date'), value: 'dueDate', width: '15%' },
        { text: this.$t('wkt.admin.tasks.table.headers.last_update'), value: 'updateDate', width: '20%' }
      ],
      statusItems: [
        { value: -1, text: this.$t('wkt.admin.tasks.filters.all_statuses') },
        { value: 0, text: this.$t('wkt.admin.tasks.status.pending') },
        { value: 1, text: this.$t('wkt.admin.tasks.status.in_progress') },
        { value: 2, text: this.$t('wkt.admin.tasks.status.completed') },
        { value: 3, text: this.$t('wkt.admin.tasks.status.canceled') }
      ],
      reminderOffsetItems: [
        { value: 10, text: this.$t('wkt.admin.tasks.reminder_offsets.10m') },
        { value: 30, text: this.$t('wkt.admin.tasks.reminder_offsets.30m') },
        { value: 60, text: this.$t('wkt.admin.tasks.reminder_offsets.1h') },
        { value: 120, text: this.$t('wkt.admin.tasks.reminder_offsets.2h') },
        { value: 1440, text: this.$t('wkt.admin.tasks.reminder_offsets.1d') }
      ],
      dueDateMenu: false,
      dueTimeMenu: false,
      scheduledDateMenu: false,
      scheduledTimeMenu: false,
      taskDialogMenuProps: {
        zIndex: 260,
        contentClass: 'task-dialog-menu',
      }
    }
  },
  computed: {
    isCreatingTask() {
      return !this.taskForm.id
    },
    taskComments() {
      if (!this.selectedTask || !this.selectedTask.comments) {
        return []
      }

      return this.selectedTask.comments
    },
    taskAttachments() {
      if (!this.selectedTask || !this.selectedTask.attachments) {
        return []
      }

      return this.selectedTask.attachments
    },
    timelineItems() {
      const timeline = []

      if (this.selectedTask && this.selectedTask.events) {
        this.selectedTask.events.forEach(event => {
          let changes = []
          if (event.eventPayloadText) {
            try {
              const parsed = JSON.parse(event.eventPayloadText)
              if (Array.isArray(parsed)) changes = parsed
            } catch (e) {
              // formato antiguo, no JSON
            }
          }
          timeline.push({
            key: `event-${event.id}`,
            type: 'event',
            date: event.creationDate,
            title: this.formatTaskEvent(event),
            actor: this.getTimelineActor(event.employeeId),
            isHuman: !!event.employeeId,
            content: changes.length ? '' : (event.eventPayloadText || ''),
            changes,
          })
        })
      }

      if (this.selectedTask && this.selectedTask.comments) {
        this.selectedTask.comments.forEach(comment => {
          timeline.push({
            key: `comment-${comment.id}`,
            type: 'comment',
            date: comment.creationDate,
            title: `${this.$t('wkt.admin.tasks.timeline.comment')} · ${this.getEmployeeName(comment.employeeId)}`,
            actor: this.getTimelineActor(comment.employeeId),
            content: comment.content,
          })
        })
      }

      return timeline.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    },
    selectedTaskHasUnreadActivity() {
      return !!(this.selectedTask && this.selectedTask.task && this.selectedTask.task.hasUnreadActivity)
    },
    selectedTaskHasReminderAlert() {
      return !!(this.selectedTask && this.selectedTask.task && this.selectedTask.task.hasReminderAlert)
    },
    selectedTaskAssigneeSummary() {
      if (!this.taskForm.assigneeEmployeeIds || !this.taskForm.assigneeEmployeeIds.length) {
        return '--'
      }

      return this.taskForm.assigneeEmployeeIds.map(employeeId => this.getEmployeeName(employeeId)).join(', ')
    },
    selectedTaskAssigneeNames() {
      if (!this.taskForm.assigneeEmployeeIds || !this.taskForm.assigneeEmployeeIds.length) {
        return []
      }

      return this.taskForm.assigneeEmployeeIds.map(employeeId => this.getEmployeeName(employeeId))
    },
    isTaskOverdue() {
      if (!this.selectedTask || !this.selectedTask.task || !this.selectedTask.task.dueDate) {
        return false
      }

      if (this.taskForm.status === 2 || this.taskForm.status === 3) {
        return false
      }

      return moment(this.selectedTask.task.dueDate).isBefore(moment())
    },
    dueDateSummary() {
      if (!this.selectedTask || !this.selectedTask.task || !this.selectedTask.task.dueDate) {
        return this.$t('wkt.admin.tasks.summary.no_due_date')
      }

      return this.formatLongDateTime(this.selectedTask.task.dueDate, true)
    },
  },
  mounted() {
    this.loadContext()
    this.loadEmployees()
    if (typeof window.addEventListener === 'function') {
      window.addEventListener('tweem-channel-message', this.handleChannelMessage, false)
    }
    this.installShadowMenuWheelPatch({
      menuSelector: '.task-dialog-menu',
      handlerKey: '__tweemTaskManualWheelHandler'
    })
  },
  beforeDestroy() {
    if (typeof window.removeEventListener === 'function') {
      window.removeEventListener('tweem-channel-message', this.handleChannelMessage, false)
    }
    this.teardownShadowMenuWheelPatch()
    if (this.realtimeRefreshTimer) {
      window.clearTimeout(this.realtimeRefreshTimer)
      this.realtimeRefreshTimer = null
    }
  },
  methods: {
    onWheel(event) {
      if (!event) {
        return
      }

      event.stopPropagation()
    },
    doShowAlert(payload) {
      this.alertText = payload.text
      this.alertColor = payload.color
      this.alertTimeout = payload.timeout
      this.showAlert = true
    },
    employeeText(item) {
      if (!item) {
        return ''
      }

      return `${item.lastname || ''}, ${item.firstname || ''}`.trim().replace(/^, /, '')
    },
    loadContext() {
      this.sessionProfile = gwt.getSessionProfile()
      const sessionInformation = gwt.getSessionInformation()
      this.sessionEmployeeId = sessionInformation && sessionInformation.employee ? sessionInformation.employee.id : null
      this.canWrite = !!(this.sessionProfile && this.matchRoleModify(this.sessionProfile, ['tasks']))
    },
    handleChannelMessage(event) {
      const detail = event && event.detail ? event.detail : null
      const messageType = detail && detail.messageType ? detail.messageType : null
      const payload = detail && detail.normalizedPayload ? detail.normalizedPayload : null

      if (!messageType || (messageType !== 'task-update' && messageType !== 'task-seen') || !payload || !payload.taskId) {
        return
      }

      if (messageType === 'task-seen') {
        this.handleTaskSeenRealtime(payload)
        return
      }

      this.handleTaskUpdatedRealtime(payload)
    },
    handleTaskSeenRealtime(payload) {
      if (!payload || !payload.taskId) {
        return
      }

      if (!this.dialogOnly) {
        this.patchListTaskNovelty(payload.taskId, false, payload.latestTaskEventId || null)
      }

      if (this.selectedTask && this.selectedTask.task && this.selectedTask.task.id === payload.taskId) {
        if (!this.selectedTask.userState) {
          this.selectedTask.userState = {}
        }
        this.selectedTask.userState.lastSeenTaskEventId = payload.latestTaskEventId || this.selectedTask.userState.lastSeenTaskEventId
        this.selectedTask.userState.lastSeenDate = new Date().toISOString()
      }
    },
    handleTaskUpdatedRealtime(payload) {
      if (!payload || !payload.taskId) {
        return
      }

      if (this.selectedTask && this.selectedTask.task && this.selectedTask.task.id === payload.taskId) {
        this.reloadSelectedTask(false)
        return
      }

      if (!this.dialogOnly) {
        this.scheduleRealtimeRefresh()
      }
    },
    scheduleRealtimeRefresh() {
      if (this.realtimeRefreshTimer) {
        window.clearTimeout(this.realtimeRefreshTimer)
      }

      this.realtimeRefreshTimer = window.setTimeout(() => {
        this.realtimeRefreshTimer = null
        this.getItems()
      }, 400)
    },
    loadEmployees() {
      const departmentsByProfile = gwt.getDepartmentsByProfile()

      if (departmentsByProfile && departmentsByProfile.length) {
        this.setEmployeesFromDepartments(departmentsByProfile)
        return
      }

      api.getStaff(
        data => {
          this.setEmployeesFromDepartments(data || [])
        },
        err => {
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    setEmployeesFromDepartments(departments) {
      const employees = []
      const seen = {}
      const normalizedDepartments = departments || []

      normalizedDepartments.forEach(department => {
        (department.employees || []).forEach(employee => {
          if (!seen[employee.id]) {
            seen[employee.id] = true
            employees.push(employee)
          }
        })
      })

      this.employees = employees.sort((a, b) => this.employeeText(a).localeCompare(this.employeeText(b)))
    },
    getEmployeeName(employeeId) {
      if (!employeeId) {
        return '--'
      }

      const employee = this.employees.find(x => x.id === employeeId)
      return employee ? this.employeeText(employee) : `#${employeeId}`
    },
    getTimelineActor(employeeId) {
      if (!employeeId) {
        return this.$t('wkt.admin.tasks.timeline.system')
      }

      return this.getEmployeeName(employeeId)
    },
    formatTaskStatus(status) {
      switch (status) {
        case 1: return this.$t('wkt.admin.tasks.status.in_progress')
        case 2: return this.$t('wkt.admin.tasks.status.completed')
        case 3: return this.$t('wkt.admin.tasks.status.canceled')
        default: return this.$t('wkt.admin.tasks.status.pending')
      }
    },
    isOpenTaskStatus(status) {
      return status === 0 || status === 1
    },
    isListTaskOverdue(task) {
      if (!task || !task.dueDate || !this.isOpenTaskStatus(task.status)) {
        return false
      }

      if (task.overdue === true) {
        return true
      }

      return moment(task.dueDate).isBefore(moment())
    },
    hasListTaskReminderAlert(task) {
      if (!task || !this.isOpenTaskStatus(task.status)) {
        return false
      }

      if (task.hasReminderAlert === true) {
        return true
      }

      return !!task.reminderSentDate
    },
    formatTaskChangeField(change) {
      const labels = {
        status: 'Estado',
        title: 'Título',
        dueDate: 'Fecha límite',
        scheduledDate: 'Fecha programada',
      }
      const label = labels[change.field] || change.field

      if (change.field === 'status') {
        const from = this.formatTaskStatus(parseInt(change.from, 10))
        const to = this.formatTaskStatus(parseInt(change.to, 10))
        return `${label}: ${from} → ${to}`
      }

      if (change.field === 'dueDate' || change.field === 'scheduledDate') {
        const from = change.from ? this.formatLongDateTime(change.from, true) : '--'
        const to = change.to ? this.formatLongDateTime(change.to, true) : '--'
        return `${label}: ${from} → ${to}`
      }

      const from = change.from || '--'
      const to = change.to || '--'
      return `${label}: ${from} → ${to}`
    },
    formatTaskEvent(event) {
      if (!event || !event.eventType) {
        return '--'
      }

      switch (event.eventType) {
        case 'CREATED':
          return this.$t('wkt.admin.tasks.timeline.created')
        case 'ASSIGNED': {
          if (event.eventPayloadText) {
            const names = event.eventPayloadText.split(',').filter(Boolean)
              .map(id => this.getEmployeeName(parseInt(id, 10))).join(', ')
            if (names) return `${this.$t('wkt.admin.tasks.timeline.assigned')}: ${names}`
          }
          return this.$t('wkt.admin.tasks.timeline.assigned')
        }
        case 'UNASSIGNED': {
          if (event.eventPayloadText) {
            const names = event.eventPayloadText.split(',').filter(Boolean)
              .map(id => this.getEmployeeName(parseInt(id, 10))).join(', ')
            if (names) return `${this.$t('wkt.admin.tasks.timeline.unassigned')}: ${names}`
          }
          return this.$t('wkt.admin.tasks.timeline.unassigned')
        }
        case 'COMMENT_ADDED':
          return this.$t('wkt.admin.tasks.timeline.comment_added')
        case 'STATUS_CHANGED': {
          // Formato legacy "0:2" (antes del JSON): mostrar detalle en el título
          if (event.eventPayloadText && !event.eventPayloadText.startsWith('[')) {
            const parts = event.eventPayloadText.split(':')
            if (parts.length === 2) {
              const from = this.formatTaskStatus(parseInt(parts[0], 10))
              const to = this.formatTaskStatus(parseInt(parts[1], 10))
              return `${this.$t('wkt.admin.tasks.timeline.status_changed')}: ${from} → ${to}`
            }
          }
          // Formato nuevo JSON: el detalle aparece en las líneas de cambio, título genérico
          return this.$t('wkt.admin.tasks.timeline.updated')
        }
        case 'TASK_BECAME_VISIBLE':
          return this.$t('wkt.admin.tasks.timeline.became_visible')
        case 'REMINDER_TRIGGERED':
          return this.$t('wkt.admin.tasks.timeline.reminder_triggered')
        default:
          return this.$t('wkt.admin.tasks.timeline.updated')
      }
    },
    toLocalDateTime(value) {
      if (!value) {
        return ''
      }

      return moment(value).format('YYYY-MM-DDTHH:mm')
    },
    fromLocalDateTime(value) {
      if (!value) {
        return null
      }

      return moment(value).toISOString()
    },
    toLocalDateTimeFromMoment(value) {
      if (!value) {
        return ''
      }

      return moment(value).format('YYYY-MM-DDTHH:mm')
    },
    getLocalDatePart(value) {
      if (!value) {
        return ''
      }

      return moment(value).format('YYYY-MM-DD')
    },
    getLocalTimePart(value, fallback = '09:00') {
      if (!value) {
        return fallback
      }

      return moment(value).format('HH:mm')
    },
    updateTaskDatePart(fieldName, datePart) {
      if (!fieldName || !datePart) {
        return
      }

      const currentTime = this.getLocalTimePart(this.taskForm[fieldName], fieldName === 'dueDate' ? '18:00' : '09:00')
      this.$set(this.taskForm, fieldName, `${datePart}T${currentTime}`)
    },
    updateTaskTimePart(fieldName, timePart) {
      if (!fieldName || !timePart) {
        return
      }

      const currentDate = this.getLocalDatePart(this.taskForm[fieldName]) || moment().format('YYYY-MM-DD')
      this.$set(this.taskForm, fieldName, `${currentDate}T${timePart}`)
    },
    setScheduledDateNow() {
      this.taskForm.scheduledDate = this.toLocalDateTimeFromMoment(moment())
    },
    applyScheduledPresetTomorrowMorning() {
      this.taskForm.scheduledDate = this.toLocalDateTimeFromMoment(moment().add(1, 'day').hour(9).minute(0).second(0).millisecond(0))
    },
    clearDueDate() {
      this.taskForm.dueDate = ''
      this.taskForm.reminderEnabled = false
    },
    applyDueDatePreset(offsetDays) {
      this.taskForm.dueDate = this.toLocalDateTimeFromMoment(
        moment().add(offsetDays, 'day').hour(18).minute(0).second(0).millisecond(0)
      )
    },
    searchTimeOut() {
      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }
      this.timer = setTimeout(() => {
        this.resetAndLoad()
      }, 800)
    },
    resetAndLoad() {
      this.options.page = 1
      this.getItems()
    },
    toggleHiddenTasks() {
      this.showHiddenTasks = !this.showHiddenTasks
      this.resetAndLoad()
    },
    refreshModule() {
      if (this.dialogOnly) {
        return
      }
      this.getItems()
    },
    getItems() {
      if (this.dialogOnly) {
        return
      }
      this.apiLoading = true
      const { page, itemsPerPage } = this.options

      api.getTasks((page ? page - 1 : 0) * itemsPerPage, itemsPerPage, this.onlyAssignedToMe, this.statusFilter,
        this.showHiddenTasks,
        this.search ? this.search : 'none',
        data => {
          this.apiLoading = false
          this.items = data.entities || []
          this.totalItems = data.total || 0
        },
        err => {
          this.apiLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    onReveal() {
      if (this.dialogOnly) {
        return
      }
      this.getItems()
    },
    open(action, payload) {
      this.loadContext()
      this.loadEmployees()

      if (action === 'CREATE') {
        this.openNewTask()
        return
      }

      if (action === 'MARK_SEEN') {
        this.markTaskAsSeenById(payload)
        return
      }

      if (payload) {
        this.openTask({ id: payload })
      }
    },
    openNewTask() {
      this.detailTab = 0
      this.detailSectionPanels = []
      this.selectedTask = {
        task: null,
        comments: [],
        attachments: [],
        events: [],
        assignees: [],
        userState: null,
      }
      this.taskForm = {
        id: null,
        title: '',
        description: '',
        status: 0,
        scheduledDate: this.toLocalDateTimeFromMoment(moment()),
        dueDate: '',
        reminderEnabled: false,
        reminderOffsetMinutes: 60,
        assigneeEmployeeIds: [],
      }
      this.newComment = ''
      this.newCommentFiles = []
      this.detailDialog = true
    },
    openTask(task) {
      if (!task || !task.id) {
        return
      }

      this.detailTab = 0
      this.detailSectionPanels = []
      this.detailDialog = true
      this.detailLoading = true
      this.newComment = ''
      this.newCommentFiles = []

      api.getTask(task.id,
        data => {
          this.detailLoading = false
          this.applySelectedTask(data)
          this.markSelectedTaskAsSeen()
        },
        err => {
          this.detailLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    applySelectedTask(data) {
      this.selectedTask = data || {
        task: null,
        comments: [],
        attachments: [],
        events: [],
        assignees: [],
        userState: null,
      }

      const task = data && data.task ? data.task : {}
      const assigneeEmployeeIds = (data && data.assignees ? data.assignees : []).map(x => x.employeeId)

      this.taskForm = {
        id: task.id || null,
        title: task.title || '',
        description: task.description || '',
        status: task.status != null ? task.status : 0,
        scheduledDate: this.toLocalDateTime(task.scheduledDate),
        dueDate: this.toLocalDateTime(task.dueDate),
        reminderEnabled: !!task.reminderEnabled,
        reminderOffsetMinutes: task.reminderOffsetMinutes != null ? task.reminderOffsetMinutes : 60,
        assigneeEmployeeIds,
      }
    },
    getCommentAttachments(commentId) {
      return this.taskAttachments.filter(attachment => attachment.taskCommentId === commentId)
    },
    getLatestEvent() {
      if (!this.selectedTask || !this.selectedTask.events || !this.selectedTask.events.length) {
        return null
      }

      return this.selectedTask.events[this.selectedTask.events.length - 1]
    },
    markSelectedTaskAsSeen() {
      const latestEvent = this.getLatestEvent()

      if (!this.selectedTask || !this.selectedTask.task || !this.selectedTask.task.id || !latestEvent) {
        return
      }

      const userState = this.selectedTask.userState
      if (userState && userState.lastSeenTaskEventId && userState.lastSeenTaskEventId >= latestEvent.id) {
        return
      }

      api.markTaskAsSeen(this.selectedTask.task.id, latestEvent.id,
        () => {
          if (!this.selectedTask.userState) {
            this.selectedTask.userState = {}
          }

          this.selectedTask.userState.lastSeenTaskEventId = latestEvent.id
          this.selectedTask.userState.lastSeenDate = new Date().toISOString()
          this.patchListTaskNovelty(this.selectedTask.task.id, false, latestEvent.id)
          this.$emit('refresh')
        },
        () => {})
    },
    patchListTaskNovelty(taskId, hasUnreadActivity, latestTaskEventId) {
      const index = this.items.findIndex(x => x.id === taskId)
      if (index < 0) {
        return
      }

      const updated = Object.assign({}, this.items[index], {
        hasUnreadActivity,
        latestTaskEventId,
      })
      this.$set(this.items, index, updated)
    },
    markTaskAsSeenById(taskId) {
      if (!taskId) {
        return
      }

      api.getTask(taskId,
        data => {
          const latestEvent = data && data.events && data.events.length ? data.events[data.events.length - 1] : null
          if (!latestEvent || !latestEvent.id) {
            return
          }

          api.markTaskAsSeen(taskId, latestEvent.id,
            () => {
              this.patchListTaskNovelty(taskId, false, latestEvent.id)
              if (this.selectedTask && this.selectedTask.task && this.selectedTask.task.id === taskId) {
                this.applySelectedTask(data)
                if (!this.selectedTask.userState) {
                  this.selectedTask.userState = {}
                }
                this.selectedTask.userState.lastSeenTaskEventId = latestEvent.id
                this.selectedTask.userState.lastSeenDate = new Date().toISOString()
              }
              this.$emit('refresh')
              this.doShowAlert({ text: this.$t('wkt.admin.tasks.messages.marked_seen'), color: 'success', timeout: 2500 })
            },
            err => {
              this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
            })
        },
        err => {
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    validateTaskForm() {
      if (!this.$refs.taskForm) {
        return false
      }

      return this.$refs.taskForm.validate()
    },
    schedulePendingTaskSave() {
      if (!this.pendingSaveAfterTabSwitch) {
        return
      }

      if (this.pendingSaveAfterTabSwitchTimer) {
        window.clearTimeout(this.pendingSaveAfterTabSwitchTimer)
        this.pendingSaveAfterTabSwitchTimer = null
      }

      this.pendingSaveAfterTabSwitchTimer = window.setTimeout(() => {
        this.pendingSaveAfterTabSwitchTimer = null

        if (!this.pendingSaveAfterTabSwitch || this.detailTab !== 0 || this.detailLoading || this.commentLoading) {
          return
        }

        this.pendingSaveAfterTabSwitch = false
        this.saveTask()
      }, 180)
    },
    saveTask() {
      if (!this.canWrite) {
        return
      }

      if (this.detailLoading || this.commentLoading) {
        return
      }

      if (this.detailTab !== 0) {
        this.pendingSaveAfterTabSwitch = true
        this.detailTab = 0
        this.$nextTick(() => {
          this.schedulePendingTaskSave()
        })
        return
      }

      if (!this.validateTaskForm()) {
        this.doShowAlert({
          text: this.$t('wkt.admin.tasks.validation.fix_errors'),
          color: 'error',
          timeout: 3000,
        })
        return
      }

      this.saveLoading = true
      const existingTask = this.selectedTask && this.selectedTask.task ? this.selectedTask.task : {}
      const request = {
        task: Object.assign({}, existingTask, {
          id: this.taskForm.id,
          title: this.taskForm.title,
          description: this.taskForm.description,
          status: this.taskForm.status,
          scheduledDate: this.fromLocalDateTime(this.taskForm.scheduledDate),
          dueDate: this.fromLocalDateTime(this.taskForm.dueDate),
          reminderEnabled: !!this.taskForm.dueDate && !!this.taskForm.reminderEnabled,
          reminderOffsetMinutes: !!this.taskForm.dueDate && !!this.taskForm.reminderEnabled
            ? this.taskForm.reminderOffsetMinutes
            : null,
        }),
        assigneeEmployeeIds: this.taskForm.assigneeEmployeeIds || [],
      }

      api.storeTask(request,
        data => {
          this.saveLoading = false
          this.applySelectedTask(data)
          this.syncTaskInList(data.task)
          this.$emit('refresh')
          this.doShowAlert({ text: this.$t('wkt.admin.tasks.messages.saved'), color: 'success', timeout: 2500 })
          this.closeDetailDialog()
        },
        err => {
          this.saveLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    syncTaskInList(task) {
      if (!task || !task.id) {
        this.getItems()
        return
      }

      const index = this.items.findIndex(x => x.id === task.id)
      if (index >= 0) {
        this.$set(this.items, index, Object.assign({}, this.items[index], task))
        return
      }

      this.getItems()
    },
    saveComment() {
      if (!this.canWrite || !this.selectedTask || !this.selectedTask.task || !this.selectedTask.task.id || !this.newComment.trim()) {
        return
      }

      if (this.validateCommentAttachments()) {
        return
      }

      this.commentLoading = true
      api.storeTaskComment(this.selectedTask.task.id, { content: this.newComment.trim() },
        storedComment => {
          this.uploadPendingCommentFiles(storedComment,
            () => {
              this.commentLoading = false
              this.newComment = ''
              this.newCommentFiles = []
              this.detailTab = 1
              this.reloadSelectedTask(true)
            },
            err => {
              this.commentLoading = false
              this.newCommentFiles = []
              this.reloadSelectedTask(false)
              this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
            })
        },
        err => {
          this.commentLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    validateCommentAttachments() {
      const validation = api.validateSupportAttachments(this.newCommentFiles)
      if (!validation) {
        return null
      }

      this.newCommentFiles = []
      this.doShowAlert({
        text: this.$t(validation.key, validation.params),
        color: 'error',
        timeout: 6000
      })
      return validation
    },
    uploadPendingCommentFiles(comment, onSuccess, onError, index = 0) {
      if (!comment || !comment.id || !this.newCommentFiles || !this.newCommentFiles.length) {
        onSuccess()
        return
      }

      if (index >= this.newCommentFiles.length) {
        onSuccess()
        return
      }

      const file = this.newCommentFiles[index]
      this.buildTaskCommentAttachmentPayload(file,
        payload => {
          api.uploadTaskCommentAttachment(this.selectedTask.task.id, comment.id, payload,
            () => {
              this.uploadPendingCommentFiles(comment, onSuccess, onError, index + 1)
            },
            onError)
        },
        onError)
    },
    buildTaskCommentAttachmentPayload(file, onSuccess, onError) {
      if (!file) {
        onError(new Error('Attachment file is required'))
        return
      }

      const reader = new FileReader()
      reader.onload = () => {
        const rawResult = typeof reader.result === 'string' ? reader.result : ''
        const contentBase64 = rawResult.includes(',') ? rawResult.split(',')[1] : rawResult

        onSuccess({
          fileName: file.name,
          contentType: file.type || null,
          contentBase64: contentBase64
        })
      }
      reader.onerror = () => {
        onError(new Error('Unable to read attachment file'))
      }
      reader.readAsDataURL(file)
    },
    downloadAttachment(commentId, attachment) {
      if (!this.selectedTask || !this.selectedTask.task || !this.selectedTask.task.id || !attachment || !attachment.id) {
        return
      }

      api.downloadTaskCommentAttachment(this.selectedTask.task.id, commentId, attachment.id,
        null,
        err => {
          this.doShowAlert({
            text: err && err.message ? err.message : this.$t('wkt.admin.application.unable_download_document'),
            color: 'error',
            timeout: 3000
          })
        })
    },
    reloadSelectedTask(showMessage) {
      if (!this.selectedTask || !this.selectedTask.task || !this.selectedTask.task.id) {
        return
      }

      this.detailLoading = true
      api.getTask(this.selectedTask.task.id,
        data => {
          this.detailLoading = false
          this.applySelectedTask(data)
          this.markSelectedTaskAsSeen()

          if (showMessage) {
            this.doShowAlert({ text: this.$t('wkt.admin.tasks.messages.comment_saved'), color: 'success', timeout: 2500 })
          }

          this.$emit('refresh')
          this.getItems()
        },
        err => {
          this.detailLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    deleteSelectedTask() {
      if (!this.canWrite || !this.selectedTask || !this.selectedTask.task || !this.selectedTask.task.id) {
        return
      }

      if (this.$refs.deleteTaskConfirmDialog) {
        this.$refs.deleteTaskConfirmDialog.confirm(this.selectedTask.task.id)
      }
    },
    confirmDeleteSelectedTask() {
      if (!this.canWrite || !this.selectedTask || !this.selectedTask.task || !this.selectedTask.task.id) {
        return
      }

      this.deleteLoading = true
      api.deleteTask(this.selectedTask.task.id,
        () => {
          this.deleteLoading = false
          this.detailDialog = false
          this.$emit('refresh')
          this.doShowAlert({ text: this.$t('wkt.admin.tasks.messages.deleted'), color: 'success', timeout: 2500 })
          this.getItems()
        },
        err => {
          this.deleteLoading = false
          this.doShowAlert({ text: err.message || err, color: 'error', timeout: 3000 })
        })
    },
    closeDetailDialog() {
      if (this.pendingSaveAfterTabSwitchTimer) {
        window.clearTimeout(this.pendingSaveAfterTabSwitchTimer)
        this.pendingSaveAfterTabSwitchTimer = null
      }
      this.pendingSaveAfterTabSwitch = false
      this.detailDialog = false
      this.detailLoading = false
      this.detailTab = 0
      this.detailSectionPanels = [0]
      this.selectedTask = null
      this.newComment = ''
      this.newCommentFiles = []
    },
  },
  watch: {
    options: {
      handler () {
        if (this.dialogOnly) {
          return
        }
        this.getItems()
      },
      deep: true,
    },
    'taskForm.dueDate' (value) {
      if (!value) {
        this.taskForm.reminderEnabled = false
      }
    },
    detailTab(value) {
      if (value === 0 && this.pendingSaveAfterTabSwitch) {
        this.schedulePendingTaskSave()
      }
    },
  },
}
</script>

<style scoped>
.tasks-list-toolbar {
  height: auto !important;
}

::v-deep(.tasks-list-toolbar .v-toolbar__content) {
  height: auto !important;
  min-height: 64px;
  width: 100%;
}

.tasks-search-field {
  flex: 1 1 260px;
  min-width: 0;
}

.tasks-hidden-button {
  flex: 0 0 auto;
}

.task-dialog-toolbar {
  row-gap: 8px;
}

.tasks-row {
  cursor: pointer;
}

.tasks-row--unread {
  background: rgba(33, 150, 243, 0.05);
}

.task-novelty-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #1976d2;
  display: inline-block;
}

.task-summary-card--alert {
  border-color: #ef5350 !important;
  background: rgba(239, 83, 80, 0.04);
}

.task-comments-history-card,
.task-comment-composer-card {
  height: 100%;
}

.task-activity-card--human {
  border-color: rgba(25, 118, 210, 0.35) !important;
  background: rgba(25, 118, 210, 0.04);
}

.task-activity-card--system {
  border-color: rgba(97, 97, 97, 0.28) !important;
  background: rgba(97, 97, 97, 0.04);
}

::v-deep(.task-dialog-menu) {
  z-index: 1025 !important;
  overflow-y: auto !important;
  overscroll-behavior: contain;
}

::v-deep(.task-dialog-menu .v-list) {
  overscroll-behavior: contain;
}
</style>
